const chapter = {
  id: 'straight-lines',
  slug: 'straight-lines',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Straight Lines',
        hindi: 'सरल रेखाएँ',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main',
        hindi: 'जेईई मेन में 1-2 प्रश्न',
  },
  syllabusTags: ['Coordinate Geometry', 'JEE-Main', 'JEE-Advanced', 'Class-11'],
  sections: [
    {
      id: 'distance-section',
      title: {
        english: 'Distance & Section Formulas',
        hindi: 'दूरी और विभाजन सूत्र',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Distance between two points',
        hindi: 'दो बिंदुओं के बीच की दूरी',
          },
          statement: {
            english: 'Distance between $A(x_1, y_1)$ and $B(x_2, y_2)$ is $AB = \\sqrt{(x_2 - x_1)^2 + (y_2 - y_1)^2}$.',
        hindi: '$A(x_1, y_1)$ और $B(x_2, y_2)$ के बीच की दूरी $AB = \\sqrt{(x_2 - x_1)^2 + (y_2 - y_1)^2}$ है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Section formula',
        hindi: 'अनुभाग सूत्र',
          },
          statement: {
            english: 'Point dividing segment $AB$ in ratio $m : n$ **internally** is $\\left(\\dfrac{mx_2 + nx_1}{m + n},\\ \\dfrac{my_2 + ny_1}{m + n}\\right)$. **Externally** in $m : n$: $\\left(\\dfrac{mx_2 - nx_1}{m - n},\\ \\dfrac{my_2 - ny_1}{m - n}\\right)$.',
        hindi: 'बिंदु विभाजन खंड $AB$ अनुपात $m: n$ **आंतरिक रूप से** $\\left(\\dfrac{mx_2 + nx_1}{m + n},\\ \\dfrac{my_2 + ny_1}{m + n}\\right)$ है। **बाह्य** $m: n$ में: $\\left(\\dfrac{mx_2 - nx_1}{m - n},\\ \\dfrac{my_2 - ny_1}{m - n}\\right)$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Midpoint**: $\\left(\\dfrac{x_1+x_2}{2},\\ \\dfrac{y_1+y_2}{2}\\right)$.',
        hindi: '**मध्यबिंदु**: $\\left(\\dfrac{x_1+x_2}{2},\\ \\dfrac{y_1+y_2}{2}\\right)$.',
            },
            {
              english: '**Centroid** of triangle with vertices $(x_i, y_i)$: $\\left(\\dfrac{x_1+x_2+x_3}{3},\\ \\dfrac{y_1+y_2+y_3}{3}\\right)$.',
        hindi: 'शीर्षों वाले त्रिभुज का **केंद्रक** $(x_i, y_i)$: $\\left(\\dfrac{x_1+x_2+x_3}{3},\\ \\dfrac{y_1+y_2+y_3}{3}\\right)$।',
            },
            {
              english: '**Incentre**: $\\left(\\dfrac{ax_1+bx_2+cx_3}{a+b+c},\\ \\dfrac{ay_1+by_2+cy_3}{a+b+c}\\right)$ where $a, b, c$ are side lengths opposite to vertices.',
        hindi: '**अंतर्केंद्र**: $\\left(\\dfrac{ax_1+bx_2+cx_3}{a+b+c},\\ \\dfrac{ay_1+by_2+cy_3}{a+b+c}\\right)$ जहां $a, b, c$ शीर्षों के विपरीत भुजाओं की लंबाई है।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Area of a triangle',
        hindi: 'एक त्रिभुज का क्षेत्रफल',
          },
          statement: {
            english: '$\\Delta = \\dfrac{1}{2}\\bigl|x_1(y_2 - y_3) + x_2(y_3 - y_1) + x_3(y_1 - y_2)\\bigr|$. Three points are **collinear** iff $\\Delta = 0$.',
        hindi: '$\\Delta = \\dfrac{1}{2}\\bigl|x_1(y_2 - y_3) + x_2(y_3 - y_1) + x_3(y_1 - y_2)\\bigr|$. तीन बिंदु **संरेख** हैं यदि $\\Delta = 0$।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Show that $(1, 2),\\ (3, 4),\\ (5, 6)$ are collinear.',
        hindi: 'दिखाएँ कि $(1, 2),\\\\ (3, 4),\\\\ (5, 6)$ संरेख हैं।',
          },
          solution: {
            english: '$\\Delta = \\tfrac{1}{2}|1(4-6) + 3(6-2) + 5(2-4)| = \\tfrac{1}{2}|-2 + 12 - 10| = 0$ ✓',
        hindi: '$\\Delta = \\tfrac{1}{2}|1(4-6) + 3(6-2) + 5(2-4)| = \\tfrac{1}{2}|-2 + 12 - 10| = 0$✓',
          },
        },
      ],
    },
    {
      id: 'slope',
      title: {
        english: 'Slope of a Line',
        hindi: 'रेखा की प्रवणता',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Slope',
        hindi: 'ढलान',
          },
          body: {
            english: 'The **slope** (or gradient) of a non-vertical line is $m = \\tan\\theta$, where $\\theta \\in [0, \\pi) \\setminus \\{\\pi/2\\}$ is the angle the line makes with the positive $x$-axis. For two points: $m = \\dfrac{y_2 - y_1}{x_2 - x_1}$ (provided $x_1 \\ne x_2$).',
        hindi: 'एक गैर-ऊर्ध्वाधर रेखा का **ढलान** (या ढाल) $m = \\tan\\theta$ है, जहां $\\theta \\in [0, \\pi) \\setminus \\{\\pi/2\\}$ वह कोण है जो रेखा सकारात्मक $x$-अक्ष के साथ बनाती है। दो बिंदुओं के लिए: $m = \\dfrac{y_2 - y_1}{x_2 - x_1}$ (प्रदान किया गया $x_1 \\ne x_2$)।',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 420 280" xmlns="http://www.w3.org/2000/svg">
  <line x1="60" y1="220" x2="360" y2="220" stroke="#374151" stroke-width="1.4"/>
  <line x1="60" y1="220" x2="60" y2="40" stroke="#374151" stroke-width="1.4"/>
  <line x1="100" y1="200" x2="320" y2="80" stroke="#4338ca" stroke-width="2.4"/>
  <circle cx="100" cy="200" r="4" fill="#dc2626"/>
  <circle cx="320" cy="80" r="4" fill="#dc2626"/>
  <text x="90" y="218" font-size="12" fill="#1f2937">P(x₁, y₁)</text>
  <text x="310" y="72" font-size="12" fill="#1f2937">Q(x₂, y₂)</text>
  <line x1="100" y1="200" x2="320" y2="200" stroke="#9ca3af" stroke-dasharray="4 3"/>
  <line x1="320" y1="200" x2="320" y2="80" stroke="#9ca3af" stroke-dasharray="4 3"/>
  <text x="180" y="216" font-size="12" fill="#047857">Δx = x₂ − x₁</text>
  <text x="328" y="140" font-size="12" fill="#b91c1c">Δy = y₂ − y₁</text>
  <path d="M 122 200 A 22 22 0 0 0 119.3 189.5" fill="none" stroke="#10b981" stroke-width="1.6"/>
  <text x="130" y="194" font-size="12" fill="#047857">θ</text>
  <text x="210" y="270" font-size="12" fill="#6b7280" text-anchor="middle">Slope:  m = tan θ = (y₂ − y₁) / (x₂ − x₁)</text>
</svg>`,
          caption: {
            english: 'Slope as the tangent of the inclination angle θ; equals rise (Δy) over run (Δx).',
        hindi: 'झुकाव कोण θ के स्पर्शरेखा के रूप में ढलान; रन (Δx) से अधिक वृद्धि (Δy) के बराबर है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Horizontal line: $\\theta = 0,\\ m = 0$.',
        hindi: 'क्षैतिज रेखा: $\\theta = 0,\\ m = 0$.',
            },
            {
              english: 'Vertical line: $\\theta = \\pi/2,\\ m$ undefined.',
        hindi: 'लंबवत रेखा: $\\theta = \\pi/2,\\ m$ अपरिभाषित.',
            },
            {
              english: 'Two lines $\\parallel$ iff $m_1 = m_2$.',
        hindi: 'दो पंक्तियाँ $\\parallel$ यदि $m_1 = m_2$।',
            },
            {
              english: 'Two lines $\\perp$ iff $m_1 m_2 = -1$ (both slopes finite).',
        hindi: 'दो पंक्तियाँ $\\perp$ यदि $m_1 m_2 = -1$ (दोनों ढलान परिमित हैं)।',
            },
            {
              english: 'Angle $\\phi$ between two lines: $\\tan\\phi = \\left|\\dfrac{m_1 - m_2}{1 + m_1 m_2}\\right|$.',
        hindi: 'दो रेखाओं के बीच का कोण $\\phi$: $\\tan\\phi = \\left|\\dfrac{m_1 - m_2}{1 + m_1 m_2}\\right|$.',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Find the angle between lines $y = 2x + 3$ and $y = -3x + 1$.',
        hindi: 'रेखाओं $y = 2x + 3$ और $y = -3x + 1$ के बीच का कोण ज्ञात कीजिए।',
          },
          solution: {
            english: '$\\tan\\phi = \\left|\\dfrac{2 - (-3)}{1 + 2 \\cdot (-3)}\\right| = \\left|\\dfrac{5}{-5}\\right| = 1 \\Rightarrow \\phi = 45°$.',
        hindi: '$\\tan\\phi = \\left|\\dfrac{2 - (-3)}{1 + 2 \\cdot (-3)}\\right| = \\left|\\dfrac{5}{-5}\\right| = 1 \\Rightarrow \\phi = 45°$.',
          },
        },
      ],
    },
    {
      id: 'forms',
      title: {
        english: 'Various Forms of a Straight Line',
        hindi: 'सरल रेखा के विभिन्न रूप',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Form', 'Equation', 'Notes'],
          rows: [
            ['Slope-intercept', '$y = mx + c$', '$c$ = $y$-intercept'],
            ['Point-slope', '$y - y_1 = m(x - x_1)$', 'Through $(x_1, y_1)$, slope $m$'],
            ['Two-point', '$y - y_1 = \\dfrac{y_2 - y_1}{x_2 - x_1}(x - x_1)$', 'Through two points'],
            ['Intercept', '$\\dfrac{x}{a} + \\dfrac{y}{b} = 1$', 'Intercepts $a, b$ on axes'],
            ['Normal', '$x\\cos\\alpha + y\\sin\\alpha = p$', '$p$ = perpendicular distance from O, $\\alpha$ its angle'],
            ['Parametric', '$\\dfrac{x - x_1}{\\cos\\theta} = \\dfrac{y - y_1}{\\sin\\theta} = r$', 'Through $(x_1, y_1)$, distance $r$ along inclination $\\theta$'],
            ['General', '$ax + by + c = 0$', 'Most flexible form'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'From general form $ax + by + c = 0$: slope $= -a/b$,  $x$-intercept $= -c/a$,  $y$-intercept $= -c/b$.',
        hindi: 'सामान्य रूप से $ax + by + c = 0$: ढलान $= -a/b$, $x$-अवरोधन $= -c/a$, $y$-अवरोधन $= -c/b$।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Find the equation of a line through $(2, 3)$ perpendicular to $3x + 4y = 7$.',
        hindi: '$(2, 3)$ से $3x + 4y = 7$ के लंबवत रेखा का समीकरण ज्ञात कीजिए।',
          },
          solution: {
            english: 'Slope of given line $= -3/4$. Required slope $= 4/3$. Equation: $y - 3 = \\tfrac{4}{3}(x - 2) \\Rightarrow 4x - 3y + 1 = 0$.',
        hindi: 'दी गई रेखा का ढलान $= -3/4$. आवश्यक ढलान $= 4/3$. समीकरण: $y - 3 = \\tfrac{4}{3}(x - 2) \\Rightarrow 4x - 3y + 1 = 0$.',
          },
        },
      ],
    },
    {
      id: 'distance-from-line',
      title: {
        english: 'Distance of a Point from a Line',
        hindi: 'रेखा से बिन्दु की दूरी',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Perpendicular distance',
        hindi: 'लंबवत दूरी',
          },
          statement: {
            english: 'Distance from $(x_0, y_0)$ to line $ax + by + c = 0$ is $d = \\dfrac{|ax_0 + by_0 + c|}{\\sqrt{a^2 + b^2}}$.',
        hindi: '$(x_0, y_0)$ से लाइन $ax + by + c = 0$ की दूरी $d = \\dfrac{|ax_0 + by_0 + c|}{\\sqrt{a^2 + b^2}}$ है।',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 420 240" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="180" x2="400" y2="180" stroke="#374151" stroke-width="1.4"/>
  <line x1="60" y1="220" x2="60" y2="40" stroke="#374151" stroke-width="1.4"/>
  <line x1="40" y1="60" x2="380" y2="200" stroke="#4338ca" stroke-width="2"/>
  <text x="370" y="50" font-size="12" fill="#3730a3">ax + by + c = 0</text>
  <circle cx="220" cy="80" r="5" fill="#dc2626"/>
  <text x="226" y="74" font-size="12" fill="#b91c1c" font-weight="600">P(x₀, y₀)</text>
  <line x1="220" y1="80" x2="266" y2="135" stroke="#10b981" stroke-width="2" stroke-dasharray="5 3"/>
  <text x="248" y="108" font-size="11" fill="#047857">d</text>
  <text x="210" y="225" font-size="12" fill="#6b7280" text-anchor="middle">d = |ax₀ + by₀ + c| / √(a² + b²)</text>
</svg>`,
          caption: {
            english: 'Perpendicular distance d from a point P(x₀, y₀) to line ax + by + c = 0.',
        hindi: 'एक बिंदु P(x₀, y₀) से रेखा ax + by + c = 0 पर लंबवत दूरी d।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Distance between parallel lines',
        hindi: 'समानांतर रेखाओं के बीच की दूरी',
          },
          statement: {
            english: 'Distance between $ax + by + c_1 = 0$ and $ax + by + c_2 = 0$ is $\\dfrac{|c_1 - c_2|}{\\sqrt{a^2 + b^2}}$.',
        hindi: '$ax + by + c_1 = 0$ और $ax + by + c_2 = 0$ के बीच की दूरी $\\dfrac{|c_1 - c_2|}{\\sqrt{a^2 + b^2}}$ है।',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Find the distance between $3x + 4y + 5 = 0$ and $3x + 4y - 10 = 0$.',
        hindi: '$3x + 4y + 5 = 0$ और $3x + 4y - 10 = 0$ के बीच की दूरी ज्ञात करें।',
          },
          solution: {
            english: '$\\dfrac{|5 - (-10)|}{\\sqrt{9 + 16}} = \\dfrac{15}{5} = 3$.',
        hindi: '$\\dfrac{|5 - (-10)|}{\\sqrt{9 + 16}} = \\dfrac{15}{5} = 3$.',
          },
        },
      ],
    },
    {
      id: 'family-of-lines',
      title: {
        english: 'Family of Lines & Concurrency',
        hindi: 'रेखाओं का परिवार और संगामिता',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'If $L_1 = 0$ and $L_2 = 0$ are two lines, then $L_1 + \\lambda L_2 = 0$ represents a **family of lines** passing through their point of intersection (for varying $\\lambda \\in \\mathbb{R}$).',
        hindi: 'यदि $L_1 = 0$ और $L_2 = 0$ दो रेखाएं हैं, तो $L_1 + \\lambda L_2 = 0$ उनके प्रतिच्छेदन बिंदु से गुजरने वाली **रेखाओं के एक परिवार** का प्रतिनिधित्व करता है (अलग-अलग $\\lambda \\in \\mathbb{R}$ के लिए)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Concurrency of three lines',
        hindi: 'तीन पंक्तियों की संगति',
          },
          statement: {
            english: 'Three lines $a_i x + b_i y + c_i = 0$ ($i = 1, 2, 3$) are **concurrent** iff $\\begin{vmatrix} a_1 & b_1 & c_1 \\\\ a_2 & b_2 & c_2 \\\\ a_3 & b_3 & c_3 \\end{vmatrix} = 0$.',
        hindi: 'तीन पंक्तियाँ $a_i x + b_i y + c_i = 0$ ($i = 1, 2, 3$) **समवर्ती** हैं यदि $\\begin{vmatrix} a_1 & b_1 & c_1 \\\\ a_2 & b_2 & c_2 \\\\ a_3 & b_3 & c_3 \\end{vmatrix} = 0$।',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'Find the equation of the line through the intersection of $x + 2y = 3$ and $3x - y = 1$, and parallel to $5x + 6y = 7$.',
        hindi: '$x + 2y = 3$ और $3x - y = 1$ के प्रतिच्छेदन से गुजरने वाली और $5x + 6y = 7$ के समानांतर रेखा का समीकरण ज्ञात कीजिए।',
          },
          solution: {
            english: 'Family: $(x + 2y - 3) + \\lambda(3x - y - 1) = 0$ ⇒ $(1 + 3\\lambda)x + (2 - \\lambda)y - (3 + \\lambda) = 0$. Slope $= -\\dfrac{1+3\\lambda}{2-\\lambda} = -\\dfrac{5}{6}$. Solve $\\lambda = 4/13$, substitute back.',
        hindi: 'परिवार: $(x + 2y - 3) + \\lambda(3x - y - 1) = 0$ ⇒ $(1 + 3\\lambda)x + (2 - \\lambda)y - (3 + \\lambda) = 0$. ढलान $= -\\dfrac{1+3\\lambda}{2-\\lambda} = -\\dfrac{5}{6}$. $\\lambda = 4/13$ को हल करें, वापस प्रतिस्थापित करें।',
          },
        },
      ],
    },
    {
      id: 'angle-bisector',
      title: {
        english: 'Angle Bisectors',
        hindi: 'कोण समद्विभाजक',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Bisectors of angles between two lines',
        hindi: 'दो रेखाओं के बीच के कोणों का समद्विभाजक',
          },
          statement: {
            english: 'Bisectors of angles between $a_1 x + b_1 y + c_1 = 0$ and $a_2 x + b_2 y + c_2 = 0$ are: $$\\dfrac{a_1 x + b_1 y + c_1}{\\sqrt{a_1^2 + b_1^2}} = \\pm \\dfrac{a_2 x + b_2 y + c_2}{\\sqrt{a_2^2 + b_2^2}}.$$',
        hindi: '$a_1 x + b_1 y + c_1 = 0$ और $a_2 x + b_2 y + c_2 = 0$ के बीच के कोणों के समद्विभाजक हैं: $\\dfrac{a_1 x + b_1 y + c_1}{\\sqrt{a_1^2 + b_1^2}} = \\pm \\dfrac{a_2 x + b_2 y + c_2}{\\sqrt{a_2^2 + b_2^2}}.$',
          },
        },
        {
          type: 'tip',
          text: {
            english: 'To identify which bisector corresponds to the **acute** vs **obtuse** angle: arrange both equations so that constants $c_1, c_2 > 0$ (or both $< 0$), then the bisector with **+** sign gives the bisector containing the **origin**; further, if $a_1 a_2 + b_1 b_2 > 0$, "+" gives the **obtuse** bisector and "−" the **acute**.',
        hindi: 'यह पहचानने के लिए कि कौन सा समद्विभाजक **तीव्र** बनाम **अधिक** कोण से मेल खाता है: दोनों समीकरणों को व्यवस्थित करें ताकि स्थिरांक $c_1, c_2 > 0$ (या दोनों $< 0$), फिर **+** चिह्न वाला द्विभाजक **मूल** वाला द्विभाजक देता है; इसके अलावा, यदि $a_1 a_2 + b_1 b_2 > 0$, "+" **अधिक** समद्विभाजक देता है और "-" **तीव्र** देता है।',
          },
        },
      ],
    },
    {
      id: 'image-foot',
      title: {
        english: 'Image of a Point & Foot of Perpendicular',
        hindi: 'बिन्दु का प्रतिबिम्ब और लम्बपाद',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Foot of perpendicular',
        hindi: 'लंब का पाद',
          },
          statement: {
            english: `Foot $F(\\bar x, \\bar y)$ of perpendicular from $(x_1, y_1)$ on $ax + by + c = 0$:
$$\\dfrac{\\bar x - x_1}{a} = \\dfrac{\\bar y - y_1}{b} = -\\dfrac{ax_1 + by_1 + c}{a^2 + b^2}.$$`,
            hindi: '$(x_1, y_1)$ से $ax + by + c = 0$ पर लंब का पाद $F(\\bar x, \\bar y)$: $$\\dfrac{\\bar x - x_1}{a} = \\dfrac{\\bar y - y_1}{b} = -\\dfrac{ax_1 + by_1 + c}{a^2 + b^2}.$$',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Image (mirror reflection)',
        hindi: 'छवि (दर्पण प्रतिबिंब)',
          },
          statement: {
            english: `Image $(x', y')$ of $(x_1, y_1)$ in the same line:
$$\\dfrac{x' - x_1}{a} = \\dfrac{y' - y_1}{b} = -\\dfrac{2(ax_1 + by_1 + c)}{a^2 + b^2}.$$`,
            hindi: '$(x_1, y_1)$ की छवि $(x\\\', y\\\')$ एक ही पंक्ति में: $$\\dfrac{x\\\' - x_1}{a} = \\dfrac{y\\\' - y_1}{b} = -\\dfrac{2(ax_1 + by_1 + c)}{a^2 + b^2}.$$',
          },
        },
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Find the image of $(1, 2)$ in the line $x + y - 5 = 0$.',
        hindi: 'पंक्ति $x + y - 5 = 0$ में $(1, 2)$ की छवि ढूंढें।',
          },
          solution: {
            english: '$a = 1, b = 1, c = -5$. $ax_1 + by_1 + c = 1 + 2 - 5 = -2$. $\\dfrac{x\' - 1}{1} = \\dfrac{y\' - 2}{1} = -\\dfrac{2(-2)}{2} = 2$. So $(x\', y\') = (3, 4)$.',
        hindi: '$ए = 1, बी = 1, सी = -5$। $ax_1 + by_1 + c = 1 + 2 - 5 = -2$. $\\dfrac{x\' - 1}{1} = \\dfrac{y\' - 2}{1} = -\\dfrac{2(-2)}{2} = 2$. तो $(x\', y\') = (3, 4)$.',
          },
        },
      ],
    },
    {
      id: 'pair-of-lines',
      title: {
        english: 'Pair of Straight Lines (intro)',
        hindi: 'सरल रेखाओं का युग्म (परिचय)',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'A homogeneous second-degree equation $ax^2 + 2hxy + by^2 = 0$ represents a **pair of straight lines passing through the origin**. The angle between them is $\\tan\\phi = \\left|\\dfrac{2\\sqrt{h^2 - ab}}{a + b}\\right|$.',
        hindi: 'एक सजातीय द्वितीय-डिग्री समीकरण $ax^2 + 2hxy + by^2 = 0$ मूल बिंदु से गुजरने वाली **सीधी रेखाओं की एक जोड़ी** का प्रतिनिधित्व करता है। उनके बीच का कोण $\\tan\\phi = \\left|\\dfrac{2\\sqrt{h^2 - ab}}{a + b}\\right|$ है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Lines are **real and distinct** if $h^2 > ab$.',
        hindi: 'यदि $h^2 > ab$ हो तो रेखाएं **वास्तविक और विशिष्ट** होती हैं।',
            },
            {
              english: '**Coincident** if $h^2 = ab$.',
        hindi: '**संयोग** यदि $h^2 = ab$। **काल्पनिक** (केवल मूल) यदि $h^2 = ab$।',
            },
            {
              english: '**Imaginary** (origin only) if $h^2 < ab$.',
        hindi: '**काल्पनिक** (केवल मूल) यदि $h^2 < ab$।',
            },
            {
              english: 'Lines are **perpendicular** iff $a + b = 0$.',
        hindi: 'रेखाएं **लंबवत** होती हैं यदि $a + b = 0$।',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: 'General second-degree $ax^2 + 2hxy + by^2 + 2gx + 2fy + c = 0$ represents a pair of lines iff $\\begin{vmatrix} a & h & g \\\\ h & b & f \\\\ g & f & c \\end{vmatrix} = 0$ and $h^2 \\ge ab$.',
        hindi: 'सामान्य द्वितीय-डिग्री $ax^2 + 2hxy + by^2 + 2gx + 2fy + c = 0$ $\\begin{vmatrix} a & h & g \\\\ h & b & f \\\\ g & f & c \\end{vmatrix} = 0$ और $h^2 \\ge ab$ होने पर रेखाओं की एक जोड़ी का प्रतिनिधित्व करती है।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'd = \\sqrt{(x_2-x_1)^2 + (y_2-y_1)^2}',
      desc: {
        english: 'Distance',
        hindi: 'दूरी',
      },
    },
    {
      latex: 'm = \\tfrac{y_2 - y_1}{x_2 - x_1}',
      desc: {
        english: 'Slope',
        hindi: 'ढलान',
      },
    },
    {
      latex: 'y - y_1 = m(x - x_1)',
      desc: {
        english: 'Point-slope',
        hindi: 'बिंदु-ढलान',
      },
    },
    {
      latex: '\\tfrac{x}{a} + \\tfrac{y}{b} = 1',
      desc: {
        english: 'Intercept form',
        hindi: 'इंटरसेप्ट फॉर्म',
      },
    },
    {
      latex: 'd = \\tfrac{|ax_0 + by_0 + c|}{\\sqrt{a^2 + b^2}}',
      desc: {
        english: 'Point-line distance',
        hindi: 'बिंदु-रेखा दूरी',
      },
    },
    {
      latex: '\\tan\\phi = \\left|\\tfrac{m_1 - m_2}{1 + m_1 m_2}\\right|',
      desc: {
        english: 'Angle between lines',
        hindi: 'रेखाओं के बीच का कोण',
      },
    },
    {
      latex: 'm_1 m_2 = -1\\ \\Rightarrow\\ \\perp',
      desc: {
        english: 'Perpendicularity',
        hindi: 'लंबवतता',
      },
    },
    {
      latex: '\\Delta = \\tfrac{1}{2}|x_1(y_2 - y_3) + \\ldots|',
      desc: {
        english: 'Triangle area',
        hindi: 'त्रिभुज क्षेत्र',
      },
    },
  ],
  pyqs: [
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Distance',
      problem: {
        english: 'Distance from origin to line $3x + 4y = 25$ is:',
        hindi: 'उद्गम से लाइन $3x + 4y = 25$ तक की दूरी है:',
      },
      answer: {
        english: '$5$',
        hindi: '$5$',
      },
      solution: {
        english: '$\\dfrac{|0 + 0 - 25|}{\\sqrt{9+16}} = \\dfrac{25}{5} = 5$.',
        hindi: '$\\dfrac{|0 + 0 - 25|}{\\sqrt{9+16}} = \\dfrac{25}{5} = 5$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Family',
      problem: {
        english: 'A line passes through $(2, 3)$ and the intersection of $x + y = 1$ and $2x - y = 4$. Find its slope.',
        hindi: 'एक लाइन $(2, 3)$ और $x + y = 1$ और $2x - y = 4$ के चौराहे से होकर गुजरती है। इसकी ढलान ज्ञात कीजिये.',
      },
      answer: {
        english: 'Intersection: $(5/3, -2/3)$. Slope $= \\dfrac{3 - (-2/3)}{2 - 5/3} = \\dfrac{11/3}{1/3} = 11$.',
        hindi: 'प्रतिच्छेदन: $(5/3, -2/3)$। ढलान $= \\dfrac{3 - (-2/3)}{2 - 5/3} = \\dfrac{11/3}{1/3} = 11$.',
      },
      solution: {
        english: 'Find intersection then use two-point slope.',
        hindi: 'चौराहा ढूंढें फिर दो-बिंदु ढलान का उपयोग करें।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Image',
      problem: {
        english: 'Image of $(2, 3)$ in line $y = x$ is:',
        hindi: 'पंक्ति $y = x$ में $(2, 3)$ की छवि है:',
      },
      answer: {
        english: '$(3, 2)$',
        hindi: '$(3, 2)$',
      },
      solution: {
        english: 'Reflection in $y = x$ swaps coordinates.',
        hindi: '$y = x$ स्वैप निर्देशांक में प्रतिबिंब।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Concurrency',
      problem: {
        english: 'For what value of $k$ are lines $x + y - 1 = 0,\\ 2x + 3y - k = 0,\\ 3x - y - 1 = 0$ concurrent?',
        hindi: '$k$ के किस मान के लिए पंक्तियाँ $x + y - 1 = 0,\\ 2x + 3y - k = 0,\\ 3x - y - 1 = 0$ समवर्ती हैं?',
      },
      answer: {
        english: '$k = 6$',
        hindi: '$k = 6$',
      },
      solution: {
        english: 'Determinant $= 0$: solve. Or find intersection of first and third $(1/2, 1/2)$ and substitute.',
        hindi: 'निर्धारक $= 0$: हल करें। या पहले और तीसरे $(1/2, 1/2)$ का प्रतिच्छेदन ढूंढें और स्थानापन्न करें।',
      },
    },
    {
      year: 2019,
      exam: 'JEE Advanced',
      tag: 'Pair of lines',
      problem: {
        english: 'Pair $2x^2 - 3xy - 2y^2 = 0$ represents two lines. The acute angle between them is:',
        hindi: 'जोड़ी $2x^2 - 3xy - 2y^2 = 0$ दो पंक्तियों का प्रतिनिधित्व करती है। उनके बीच का न्यूनकोण है:',
      },
      answer: {
        english: '$\\pi/2$',
        hindi: '$\\pi/2$',
      },
      solution: {
        english: '$a + b = 2 - 2 = 0 \\Rightarrow$ lines are perpendicular.',
        hindi: '$a + b = 2 - 2 = 0 \\Rightarrow$ रेखाएँ लंबवत हैं।',
      },
    },
  ],
  next: {
    slug: 'circles',
    title: 'Circles',
  },
};

export default chapter;
